import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { KineticHeading } from "@/components/motion/KineticHeading";

const GROUPS = [
  {
    title: "Ordering & the bespoke process",
    items: [
      ["How does ordering a bespoke bangle work?", "It's a four-step, white-glove process. 1) You place your order online. 2) Within 1–2 business days our expert craftspeople personally contact you to take your exact wrist measurements and discuss any unique details you'd like incorporated. 3) Your piece is handcrafted by hand. 4) It's shipped to you with tracking. Nothing is mass-produced — each bangle is made to order, for you."],
      ["Do you take my measurements before or after I buy?", "After. Placing your order secures your piece and starts the process; our team then reaches out to capture your exact wrist size and any personalisation before crafting begins."],
      ["Can I add personal details or engraving?", "Yes. You can note ideas at checkout in the 'Personalisation & special requests' field, and we'll discuss every detail with you directly — including incorporating your own horse's hair on selected pieces."],
      ["How long does a bespoke piece take?", "Up to 8 weeks from the moment your measurements and design are confirmed. Lifestyle items such as candles and cushions are in stock and ship in 2–4 business days."],
    ],
  },
  {
    title: "Pricing & the live gold rate",
    items: [
      ["Why can the price change slightly?", "Our bangles are solid gold and made to your wrist size, so two things affect the final price: the live gold rate (which moves daily) and your exact gold weight once measured. The listed price is anchored to the September 2025 gold rate and adjusts from there."],
      ["When is my final price confirmed?", "Your exact, final price is confirmed when we take your measurements and finalise any customisation — before any crafting begins. There are no surprises."],
      ["Do the prices include VAT?", "Prices are shown excluding VAT. Any applicable taxes and shipping are arranged and confirmed with you after purchase."],
      ["What currency are prices in?", "All prices are in Euro (€)."],
    ],
  },
  {
    title: "Materials, gold & diamonds",
    items: [
      ["What gold do you use?", "Solid yellow gold — 14ct on the Gilton and Ooh La La bangles, and 9ct on the Going Global. Karat is listed on each product page."],
      ["Tell me about the diamonds on the Ooh La La.", "The Ooh La La is set with two 0.0075ct VSI-quality diamonds in the eyes for a subtle yet striking sparkle, included in the price."],
      ["How do I care for my bangle?", "Store it in the supplied pouch, clean gently with a soft jewellery cloth, and avoid perfume, chlorine and abrasive cleaners."],
    ],
  },
  {
    title: "Payment, shipping & returns",
    items: [
      ["How do I pay?", "Securely via Stripe at checkout, with card and express wallets (Apple Pay, Google Pay, Link) where supported by your device."],
      ["Do you ship worldwide?", "Yes — we're proudly based in Ireland and ship worldwide, with tracking on every order."],
      ["What is your returns policy?", "Bespoke gold jewellery (all bangles) is non-returnable and non-refundable, as each piece is handmade to your personal specifications — sizing, horsehair and more. Our lifestyle items (the candle and cushion) may be returned within 14 days of delivery if unopened, unused and in original condition. See our full Returns & Refunds page for details."],
    ],
  },
];

export default function Faq() {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: GROUPS.flatMap((g) => g.items).map(([q, a]) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };
  return (
    <div className="pt-36 pb-28 px-6 md:px-12 lg:px-24 max-w-[1100px] mx-auto">
      <Seo title="FAQ" description="Everything about Equine Evolve's bespoke gold bangles — the made-to-order process, live gold-rate pricing, materials, care, shipping and more." jsonLd={faqJsonLd} />
      <Reveal>
        <p className="ee-overline mb-4">Help & answers</p>
        <KineticHeading as="h1" text="Frequently asked questions" className="font-display text-4xl md:text-6xl tracking-tight flex flex-wrap" stagger={0.05} />
        <p className="text-[#A8A29E] mt-6 max-w-2xl leading-relaxed">
          Everything you need to know about our bespoke gold bangles, live gold-rate pricing, and the
          white-glove process behind each piece.
        </p>
      </Reveal>

      <div className="mt-14 space-y-14" data-testid="faq-page">
        {GROUPS.map((g) => (
          <Reveal key={g.title}>
            <div>
              <h2 className="font-display text-2xl md:text-3xl tracking-tight ee-gold-text mb-4">{g.title}</h2>
              <Accordion type="single" collapsible className="border-t ee-hairline">
                {g.items.map(([q, a], i) => (
                  <AccordionItem key={i} value={`${g.title}-${i}`} className="ee-hairline" data-testid={`faq-item-${g.title.split(" ")[0].toLowerCase()}-${i}`}>
                    <AccordionTrigger className="font-display text-lg text-[#FAFAF9] hover:no-underline hover:text-[#D4AF37] py-5">
                      {q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#A8A29E] leading-relaxed text-base pb-6">
                      {a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal>
        <div className="mt-16 border ee-hairline bg-[#1C1917] p-10 text-center">
          <p className="ee-overline mb-3">Still have a question?</p>
          <h3 className="font-display text-2xl md:text-3xl tracking-tight mb-6">We'd love to help personally.</h3>
          <Link to="/contact-us" data-testid="faq-contact-btn" className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0C0A09] px-9 py-4 uppercase tracking-[0.15em] text-sm hover:bg-[#B5952F] transition-colors">
            Contact us <ArrowRight size={16} />
          </Link>
        </div>
      </Reveal>
    </div>
  );
}
